import { onlineManager } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { ApiError } from '#features/api/utils/ApiError';
import {
  isServerUnreachable,
  markServerUnreachable,
  setReachVerifier,
  subscribeServerReach,
} from '../model/connectivity';

/**
 * Вопрос шлюзу, на который он не может ответить не работая.
 *
 * 🔴 Раньше спрашивали `{ __typename }`. Это был неверный вопрос: схему шлюз отдаёт из памяти и
 * отвечает мгновенно даже под перегрузкой, когда настоящие чтения уже не укладываются в свои
 * двадцать секунд. Проверка бодро гасила полосу, сайт перезапрашивал весь экран, чтения снова
 * падали по таймауту - и так по кругу примерно раз в 45 секунд у каждого открытого клиента.
 *
 * `me` идёт через резолвер и клиент к сервису пользователей, то есть меряет то же, что и
 * настоящее чтение. Ответ годится любой, включая отказ: доказательство - сам факт ответа
 * (`gqlFetcher` зовёт `markNetworkProven`), а не его содержимое, поэтому анонимному посетителю
 * проверка работает так же.
 */
const reachProbeDoc = graphql(`
  query ReachProbe {
    user {
      me {
        __typename
      }
    }
  }
`);

const probeGateway = () => gqlFetcher(reachProbeDoc, null, { silent: true });

/**
 * Спросить шлюз прямо сейчас, по просьбе человека («Повторить»). Ответ любого вида снимает
 * «сервер недоступен» и отпускает чтения с паузы (`markNetworkProven` зовёт сам `gqlFetcher`);
 * молчание оставляет всё как есть. Ошибка наружу не выходит: решение уже принято в соединении.
 */
export const probeReachNow = async (): Promise<void> => {
  try {
    await probeGateway();
  } catch {
    // провал уже засчитан самим запросом
  }
};

/**
 * Паузы между проверками: первая через 3 с (VPN, поднятый на старте, обычно успевает за это
 * время), потом через 10, дальше каждые 30.
 */
export const REACH_PROBE_DELAYS_MS = [3_000, 10_000, 30_000] as const;

const delayFor = (attempt: number): number =>
  REACH_PROBE_DELAYS_MS[Math.min(attempt, REACH_PROBE_DELAYS_MS.length - 1)];

/**
 * Пока сервер недоступен, спрашивать его: по таймеру с растущей паузой и сразу - когда приложение
 * вернулось на экран и когда браузер сказал, что сеть появилась. Ответ снимает признак, а
 * переход react-query в "сеть есть" запускает `catchUp`.
 *
 * Заодно событие `online` больше не отпускает чтения, пока сервер недоступен: иначе браузер,
 * сменивший одну нерабочую сеть на другую, запускал бы полный перезапрос, который весь упадёт.
 * Отпустит их ответ проверки.
 *
 * Возвращает отключение.
 */
export const installReachProbe = (probe: () => Promise<unknown> = probeGateway): (() => void) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let attempt = 0;
  let inFlight = false;

  const stopTimer = () => {
    clearTimeout(timer);
    timer = undefined;
  };

  const schedule = () => {
    stopTimer();
    timer = setTimeout(() => {
      timer = undefined;
      // Свёрнутому приложению спрашивать незачем: вернувшись на экран, оно спросит сразу
      // (`onVisible`), а до тех пор таймер больше не ставится.
      if (document.visibilityState !== 'hidden') {
        void check();
      }
    }, delayFor(attempt));
  };

  async function check(): Promise<void> {
    if (inFlight || !isServerUnreachable()) {
      return;
    }
    inFlight = true;
    stopTimer();
    try {
      await probe();
    } catch {
      // провал уже засчитан самим запросом
    } finally {
      inFlight = false;
    }
    if (isServerUnreachable()) {
      attempt += 1;
      schedule();
    }
  }

  const onReachChange = () => {
    attempt = 0;
    if (isServerUnreachable()) {
      schedule();
    } else {
      stopTimer();
    }
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      void check();
    }
  };

  const onBrowserOnline = () => void check();

  /**
   * Перепроверка по просьбе таймаута: ответ не пришёл в срок, и решаем мы.
   *
   * Прошла - сервер жив, просто медленный, и полосы человек не видит. Не прошла - он и правда
   * недоступен, и голос проверки здесь окончательный: у одного таймаута своего голоса больше
   * нет, ради чего всё и затевалось.
   */
  const verify = async (): Promise<void> => {
    if (inFlight || isServerUnreachable()) {
      return;
    }
    inFlight = true;
    try {
      await probe();
    } catch (error) {
      /*
        🔴 Отказ - ещё не молчание. Анонимному посетителю шлюз отвечает на `me` отказом
        `user.not.auth`, и это ПОЛНОЦЕННЫЙ ответ: сервер жив, `markNetworkProven` его уже
        засчитал. Объявить тут недоступность значило бы вешать полосу «нет связи» каждому, кто
        не вошёл, на здоровом сервере. Недоступность - только когда ответа не было вовсе
        (`ApiError` со статусом 0 или обрыв на уровне сети).
      */
      if (error instanceof ApiError && error.status !== 0) {
        return;
      }
      markServerUnreachable();
    } finally {
      inFlight = false;
    }
  };

  const unsubscribe = subscribeServerReach(onReachChange);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onBrowserOnline);
  setReachVerifier(() => void verify());

  /*
    Отключение этот слушатель не снимает и прежний не возвращает: у `onlineManager` нет способа
    получить слушатель по умолчанию обратно. Это и не нужно: установка одна на страницу (корневой
    маршрут), а без недоступного сервера новый слушатель ведёт себя ровно как стоковый - `online`
    отпускает чтения, `offline` ставит на паузу.
  */
  onlineManager.setEventListener((setOnline) => {
    const online = () => setOnline(!isServerUnreachable());
    const offline = () => setOnline(false);
    window.addEventListener('online', online, false);
    window.addEventListener('offline', offline, false);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  });

  if (isServerUnreachable()) {
    schedule();
  }

  return () => {
    stopTimer();
    unsubscribe();
    setReachVerifier(null);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onBrowserOnline);
  };
};
